import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Customer } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromCustomer, toCustomerUpdate } from '../api/mappers';
import { useUpdateCustomer } from '../hooks/use-customer-mutations';
import {
  customerProfileSchema,
  type CustomerProfileFormInput,
  type CustomerProfileFormOutput,
} from '../schemas/customer-form-schema';
import { isDisabling, statusChoices } from '../schemas/customer-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'firstName',
  'lastName',
  'phone',
  'acceptsMarketing',
  'savedRingSize',
  'status',
]);

export interface EditCustomerDialogProps {
  storeId: string;
  customer: Customer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Names, phone, ring size, marketing consent and status. Disabling asks for a second confirmation. */
export function EditCustomerDialog({
  storeId,
  customer,
  open,
  onOpenChange,
}: EditCustomerDialogProps) {
  const { t } = useTranslation(['customers', 'common']);
  const message = useErrorMessage();
  const update = useUpdateCustomer(storeId, 'customers:toasts.updated');
  const [pending, setPending] = useState<CustomerProfileFormOutput | null>(null);
  const form = useForm<CustomerProfileFormInput, unknown, CustomerProfileFormOutput>({
    resolver: useI18nResolver(customerProfileSchema),
    defaultValues: fromCustomer(customer),
  });

  const close = () => {
    onOpenChange(false);
    update.reset();
  };

  const save = async (values: CustomerProfileFormOutput) => {
    try {
      await update.mutateAsync({ id: customer.id, body: toCustomerUpdate(values, customer) });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  };

  const submit = form.handleSubmit(async (values) => {
    if (isDisabling(customer.status, values.status)) {
      setPending(values);
      return;
    }
    await save(values);
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('customers:edit.title')}</DialogTitle>
          <DialogDescription>
            {t('customers:edit.description', { email: customer.email })}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers:fields.firstName')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customers:fields.lastName')}</FormLabel>
                    <FormControl>
                      <Input autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers:fields.phone')}</FormLabel>
                  <FormControl>
                    <Input type="tel" autoComplete="off" {...field} />
                  </FormControl>
                  <FormDescription>{t('customers:fields.phoneHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="savedRingSize"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers:fields.savedRingSize')}</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" placeholder="6.5" className="sm:w-40" {...field} />
                  </FormControl>
                  <FormDescription>{t('customers:fields.savedRingSizeHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="acceptsMarketing"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border p-3">
                  <div className="flex flex-col gap-1">
                    <FormLabel>{t('customers:fields.acceptsMarketing')}</FormLabel>
                    <FormDescription>{t('customers:fields.acceptsMarketingHint')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('customers:fields.status')}</FormLabel>
                  <Select value={field.value} onValueChange={(value) => field.onChange(value)}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {statusChoices(customer.status).map((status) => (
                        <SelectItem key={status} value={status}>
                          {t(`customers:status.${status}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {customer.status === 'guest'
                      ? t('customers:fields.statusGuestHint')
                      : t('customers:fields.statusHint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
        <AlertDialog open={pending !== null} onOpenChange={(next) => !next && setPending(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('customers:disable.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('customers:disable.description', { email: customer.email })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common:actions.back')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  const values = pending;
                  setPending(null);
                  if (values) void save(values);
                }}
              >
                {t('customers:disable.submit')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
